<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useProfileStore } from '~/stores/profileState'

/**
 * Standard page frame: header, full-width content, footer. Pages compose the
 * Sofax bands themselves (`.shell` inside each section), so the layout does
 * not constrain width. The whole frame is inside `.sofax`, which sets the
 * marketing typography; the CV editor uses no layout and is unaffected.
 */
const { t } = useI18n()
const router = useRouter()
const profileStore = useProfileStore()
const { checkPremiumStatus } = usePremiumStatus()

const showError500 = ref(false)

// Injektujeme hodnotu z error-handler pluginu
const nuxtApp = useNuxtApp()
nuxtApp.vueApp.provide('showError500', showError500)

// Handler pro zkušení opětovného načtení stránky
function _handleRetry() {
  showError500.value = false
  window.location.reload()
}

const props = defineProps({
  showBackButton: {
    type: Boolean,
    default: true
  }
})

/**
 * Admin pages go up to their section rather than back in history: an admin
 * page opened from a bookmark or a new tab has no history to go back to.
 * /admin/blog/* → /admin/blog, any other /admin/* → /admin.
 */
const route = useRoute()
const adminParent = computed(() => {
  const path = route.path.replace(/\/+$/, '')
  if (!path.startsWith('/admin/')) return null
  if (path.startsWith('/admin/blog/')) return '/admin/blog'
  return '/admin'
})

function goBack() {
  if (adminParent.value) return navigateTo(adminParent.value)
  router.back()
}

// Hidden while the profile is incomplete, so the user finishes it first.
const shouldShowBackButton = computed(() => {
  return props.showBackButton && !profileStore.isProfileIncomplete
})

/*
 * The presell offer (50 % off the monthly subscription) is retired together with
 * the monthly plan itself; a cancelled-on-day-one subscription undercut the
 * Job Search Pack. The injections stay so child components keep working.
 */
const { wasSpecialOfferShown } = usePremiumModals()

provide('showPresellModal', () => {})
provide('getPresellState', async () => ({
  presellShown: await wasSpecialOfferShown(),
  bubbleVisible: false,
}))

onMounted(async () => {
  await checkPremiumStatus()
})
</script>

<template>
  <div class="sofax flex min-h-screen flex-col bg-white dark:bg-gray-950">
    <AppNavbar />
    <main class="grow">
      <div v-if="shouldShowBackButton" class="shell pt-6">
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-semibold text-muted transition-colors hover:text-ink dark:text-gray-400 dark:hover:text-white"
          @click="goBack"
        >
          <i class="bi bi-arrow-left" />
          {{ t('common.backButton') }}
        </button>
      </div>
      <slot />
    </main>
    <AppFooter />

    <!-- Chat Widget -->
    <LazyIdleCommonChatWidget hydrate-on-idle />
  </div>
</template>
